import { FC, useCallback, useEffect, useState } from 'react';

import { DatePicker, Typography, Form as FormAntd, Tooltip, Popover } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import { ColSize, RowSize } from '..';
import { ColumnSizes } from '../../../pages/production/InsurancePolicyDetail/utils/renderSections';
import { FiltersSkeleton } from '../Skeletons';
import { Form, InputForm, InputNumberForm, SelectForm } from '../Form';

import { InputFactoryProp } from '../../interfaces';

import { COLORS } from '../../../config/themes/colors';
import './index.css';

type GenericObject = {
  [key: string]: any;
};

interface InputFactoryProps {
  items: Array<InputFactoryProp>;
  onFilter: (...params: any) => void;
  isLoading?: boolean;
  initialState: GenericObject;
  form: any;
  columnSizes?: ColumnSizes;
  onClear?: () => void;
  className?: string;
  clearButtonLabel?: string;
  filterButtonLabel?: string;
  hasLabelFilterBy?: boolean;
  isDisable?: boolean;
}

export const InputFactory: FC<InputFactoryProps> = ({
  items,
  onFilter = () => {},
  isLoading,
  initialState = {},
  form,
  columnSizes,
  onClear = () => {},
  className,
  clearButtonLabel = 'Borrar Filtros',
  filterButtonLabel = 'Filtrar',
  hasLabelFilterBy = true,
  isDisable = false
}) => {
  const [canShowButtonDisable, setCanShowButtonDisable] = useState(true);
  const dateFormat: string = 'DD/MM/YYYY';
  const hourFormat: string = 'HH:mm';
  dayjs.extend(customParseFormat);

  const _renderInput = (item: InputFactoryProp) => (
    <>
      {item.type === 'number' ? (
        <InputNumberForm
          key={`input-${item.placeHolder}`}
          formProps={{
            name: item.name,
            label: item.label,
            initialValue: item.value,
            style: { width: '100%',display: item.hidden ? "none" : "" },
            rules: item.rules,
          }}
          inputNumberProps={{
            placeholder: item.placeHolder,
            className: `input-hover-highlight ${item.className || ''}`,
            style: { width: '100%', height: '100%' },
            suffix: item.icon,
            controls: false,
            formatter: undefined,
            parser: undefined,
            onPressEnter: onPressFilter,
            disabled: isDisable
          }}
        />
      ) : (
        <InputForm
          formProps={{
            name: item.name,
            label: item.label,
            initialValue: item.value,
            style: { width: '100%' },
            rules: item.rules,
          }}
          inputProps={{
            type: item.type,
            placeholder: item.placeHolder,
            className: `input-hover-highlight ${item.className || ''}`,
            style: { width: '100%', height: '100%',display: item.hidden ? "none" : "" },
            suffix: item.icon,
            onPressEnter: onPressFilter,
            disabled:  item.disabled ?? isDisable,
            maxLength: item.maxLength
          }}
        />
      )}
    </>
  );

  const _renderDatePicker = (item: InputFactoryProp) => (
    <Popover content={item.tooltip} trigger="hover">
      <FormAntd.Item
        key={`date-${item.placeHolder}`}
        name={item.name}
        label={item.label}
        style={{ width: '100%',display: item.hidden ? "none" : "" }}
        rules={item.rules}
        className=''
      >

        <DatePicker.RangePicker
          allowClear={false}
          format={dateFormat}
          style={{ color: COLORS.neutrals[70], width: '100%', height: '100%' }}
          maxDate={dayjs()}
          className={item.className || ''}
          disabled={isDisable}
          getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
        />
      </FormAntd.Item>
    </Popover>
  );

  const _renderDatePickerOne = (item: InputFactoryProp) => (
    <FormAntd.Item
      key={`date-one-${item.placeHolder}`}
      name={item.name}
      label={item.label}
      initialValue={item.value}
      style={{ width: '100%',display: item.hidden ? "none" : "" }}
      rules={item.rules}
    >
      <DatePicker
        allowClear={item.allowClear || false}
        format={ item.format || dateFormat}
        style={{ color: COLORS.neutrals[70], width: '100%', height: '100%' }}
        maxDate={item.maxDate ? item.maxDate : dayjs()}
        minDate={item.minDate ? item.minDate : undefined}
        placeholder={item.placeHolder}
        mode={item.mode}
        picker={item.picker}
      />
    </FormAntd.Item>
  );

  const _renderHourPicker = (item: InputFactoryProp) => (
    <FormAntd.Item
      key={`hour-${item.placeHolder}`}
      name={item.name}
      label={item.label}
      initialValue={item.value}
      style={{ width: '100%' ,display: item.hidden ? "none" : ""}}
      rules={item.rules}
    >
      <DatePicker
        format={hourFormat}
        style={{ color: COLORS.neutrals[70], width: '100%', height: '100%' }}
        picker='time'
        showTime={{ format: 'HH:mm' }}
        popupClassName="time-popup-picker"
      />
    </FormAntd.Item>
  );

  const _renderSelect = (item: InputFactoryProp) => (
    <SelectForm
      key={`select-${item.placeHolder}`}
      formProps={{
        name: item.name,
        label: item.label,
        initialValue: item.value,
        style: { width: '100%',display: item.hidden ? "none" : ""},
        rules: item.rules,
      }}
      selectProps={{
        disabled: !Boolean(item.items?.length) || isDisable,
        placeholder: item.placeHolder,
        options: item.items,
        mode: item.isMulti ? 'multiple' : undefined,
        allowClear: item.allowClear ? false : true,
        tokenSeparators: [','],
        style: { color: COLORS.neutrals[70], width: '100%', height: '100%' },
        maxTagCount: 'responsive',
        className: `custom-select ${item.className || ''}`,
        value: item.value,
        optionFilterProp: 'label',
        loading: item.items?.length === 0,
        showSearch: item.showSearch
      }}
      allowAutoSelect={false}
    />
  );

  const onPressFilter = useCallback(() => {
    form.submit();
  }, [form]);

  const onClearFilters = () => {
    form.resetFields();
    onClear();
    setCanShowButtonDisable(true);
  };

  useEffect(() => {
    if (isLoading === false) onPressFilter();
  }, [isLoading, onPressFilter]);

  const handleValuesChange = () => {
    const currentValues = form.getFieldsValue();

    const initialStateModified = Object.keys(initialState).reduce(
      (acc: any, key: any) => {
        acc[key] =
          initialState[key] === null ||
          initialState[key] === undefined ||
          (Array.isArray(initialState[key]) && initialState[key].length === 0)
            ? ''
            : initialState[key];
        return acc;
      },
      {}
    );

    const currentValuesModified = Object.keys(currentValues).reduce(
      (acc: any, key: any) => {
        acc[key] =
          currentValues[key] === null ||
          currentValues[key] === undefined ||
          (Array.isArray(currentValues[key]) && currentValues[key].length === 0)
            ? ''
            : currentValues[key];
        return acc;
      },
      {}
    );

    const isModified = Object.keys(currentValuesModified).every((key) => {
      if (key === 'date') {
        if (
          Array.isArray(currentValuesModified[key]) &&
          Array.isArray(initialStateModified[key])
        ) {
          const currentDateRange = currentValuesModified[key]?.map(
            (dateStr: string) => dayjs(dateStr).format('YYYY-MM-DD')
          );

          const initialDateRange = initialStateModified[key]?.map(
            (dateStr: string) => dayjs(dateStr).format('YYYY-MM-DD')
          );

          return (
            currentDateRange &&
            initialDateRange &&
            currentDateRange[0] === initialDateRange[0] &&
            currentDateRange[1] === initialDateRange[1]
          );
        }
      }

      if (
        Array.isArray(currentValuesModified[key]) &&
        Array.isArray(initialStateModified[key])
      ) {
        return (
          JSON.stringify(currentValuesModified[key]) ===
          JSON.stringify(initialStateModified[key])
        );
      }

      return currentValuesModified[key] === initialStateModified[key];
    });

    setCanShowButtonDisable(isModified);
  };

  return (
    <div style={{ width: '100%' }} className={className}>
      {isLoading ? (
        <FiltersSkeleton />
      ) : items?.length ? (
        <Form
          formProps={{
            form: form,
            initialValues: initialState,
            onFinish: onFilter,
            onValuesChange: handleValuesChange,
          }}
        >
          {hasLabelFilterBy && (
            <Typography.Text strong style={{ fontSize: 16, color: '#333333' }}>
              Filtrar por:
            </Typography.Text>
          )}
          <RowSize
            rowProps={{
              gutter: [24, 0],
              wrap: true,
            }}
            className='center-justify'
            fullWidth={false}
          >
            {items?.map((item: InputFactoryProp) => {
              const columnProps = {
                style: {
                  marginTop: 10,
                  ...(item.hidden ? { display: 'none' } : {}),
                },
                ...columnSizes,
                ...item.columnSize,
              };

              return (
                <ColSize
                  size={25}
                  colProps={columnProps}
                  key={`input-${item.name}`}
                >
                  <RowSize
                    key={`input-row-${item.name}`}
                    rowProps={{
                      gutter: [0, 0],
                    }}
                  >
                    {item.element.toLocaleLowerCase() === 'input' &&
                      _renderInput(item)}
                    {item.element.toLocaleLowerCase() === 'date' &&
                      _renderDatePicker(item)}
                    {item.element.toLocaleLowerCase() === 'onlydate' &&
                      _renderDatePickerOne(item)}
                    {item.element.toLocaleLowerCase() === 'select' &&
                      _renderSelect(item)}
                    {item.element.toLocaleLowerCase() === 'hour' &&
                      _renderHourPicker(item)}
                  </RowSize>
                </ColSize>
              );
            })}
          </RowSize>
          <RowSize
            className='center-justify'
            rowProps={{ wrap: true, gutter: [0, 0] }}
          >
            <ColSize
              size={100}
              colProps={{
                xs: 24,
              }}
            >
              <RowSize
                rowProps={{
                  gutter: [0, 0],
                  justify: 'end',
                  style: {
                    marginTop: 10,
                    columnGap: 10,
                  },
                }}
              >
                <ActionButton
                  onClick={onClearFilters}
                  label={clearButtonLabel}
                  icon={<DeleteOutlined />}
                  className='btn-principal-outline-barbuss filters-buttons'
                  type='secondary'
                  disabled={canShowButtonDisable}
                  style={canShowButtonDisable ? { opacity: 0.3 } : {}}
                />
                <ActionButton
                  onClick={onPressFilter}
                  label={filterButtonLabel}
                  className='btn-principal-barbuss filters-buttons'
                />
              </RowSize>
            </ColSize>
          </RowSize>
        </Form>
      ) : (
        <></>
      )}
    </div>
  );
};
